import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import { Table, Button, Input, Dropdown, Menu, message, DatePicker, Tooltip, Empty } from "antd";
import moment from "moment";
import AddEditExpensePopup from "./components/popups/AddExpensesPopup";
import axios from "axios";
import debounce from "lodash/debounce";
import Swal from "sweetalert2";
import { ArrowDownCircle, ArrowUpCircle } from "react-feather";
import {
  DownOutlined,
  ReloadOutlined,
  CalendarOutlined,
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  WalletOutlined,
  BankOutlined,
  CreditCardOutlined,
  DollarCircleOutlined,
  AccountBookFilled as ReceiptOutlined,
} from "@ant-design/icons";
import exportService from "../../pages/pos/utils/exportService";
import DayBookPreview from "./reports/DayBookPreview";

const { Search } = Input;
const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";

const safeNumber = (value) => Number(value) || 0;
const getCurrency = () => localStorage.getItem("currencycode") || "₹";
const formatMoney = (value, currency = getCurrency()) =>
  `${currency}${safeNumber(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getBusinessDate = () => {
  const stored = localStorage.getItem("posbusiness_date") || localStorage.getItem("posbusiness_date") || moment().format("YYYY-MM-DD");
  const parsed = moment(stored);
  return parsed.isValid() ? parsed : moment();
};

const savePosBusinessDate = (date) => {
  const parsed = moment(date);
  const formatted = parsed.isValid() ? parsed.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
  localStorage.setItem("posbusiness_date", formatted);
  localStorage.removeItem("business_date");
  return formatted;
};

const selectStyles = {
  container: (provided) => ({ ...provided, width: 220 }),
  control: (provided, state) => ({
    ...provided,
    minHeight: 42,
    borderRadius: 14,
    borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.10)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
    background: "#fff",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 2147483647 }),
  menu: (provided) => ({ ...provided, zIndex: 2147483647, borderRadius: 14, overflow: "hidden", boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)" }),
  option: (provided, state) => ({
    ...provided,
    fontSize: 13,
    fontWeight: state.isSelected ? 900 : 760,
    backgroundColor: state.isSelected ? "#2563eb" : state.isFocused ? "#eff6ff" : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#0f172a",
  }),
};

const DayBook = () => {
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [stats, setStats] = useState({ upi_collection: 0, bank_transfer: 0, cash_collection: 0, company_billing: 0, expenses: 0, cash_on_drawer: 0 });
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [paymentModeFilter, setPaymentModeFilter] = useState(null);
  const [isExpensePopupVisible, setIsExpensePopupVisible] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getBusinessDate);

  const currency = getCurrency();
  const selectedDateKey = selectedDate.format("YYYY-MM-DD");

  const headers = [
    { key: "category", title: "Particulars" },
    { key: "payment_mode", title: "Mode" },
    { key: "amount", title: "Amount" },
    { key: "payment_reference", title: "Reference" },
  ];

  const paymentModes = [
    { value: "CASH", label: "Cash" },
    { value: "UPI", label: "UPI" },
    { value: "BANK_TRANSFER", label: "Bank Transfer" },
    { value: "CARD", label: "Card" },
    { value: "CHEQUE", label: "Cheque" },
    { value: "COMPANY", label: "Company Credit" },
  ];

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${apiURL}/pos/transactions/daybook`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDateKey },
      });
      if (!response.data.success) throw new Error("Failed to fetch transactions");
      const rows = (response.data.transactions || []).map((txn) => ({ ...txn, amount: safeNumber(txn.amount) }));
      setTransactions(rows);
      setAllTransactions(rows);
      setStats({
        upi_collection: safeNumber(response.data.stats?.upi_collection),
        bank_transfer: safeNumber(response.data.stats?.bank_transfer),
        cash_collection: safeNumber(response.data.stats?.cash_collection),
        company_billing: safeNumber(response.data.stats?.company_billing),
        expenses: safeNumber(response.data.stats?.expenses),
        cash_on_drawer: safeNumber(response.data.stats?.cash_on_drawer),
      });
    } catch (error) {
      Swal.fire({ title: "Unable to load day book", text: "Failed to fetch transactions. Please try again.", icon: "error", confirmButtonColor: "#2563eb", customClass: { popup: "ns-daybook-swal" } });
      setTransactions([]);
      setAllTransactions([]);
      setStats({ upi_collection: 0, bank_transfer: 0, cash_collection: 0, company_billing: 0, expenses: 0, cash_on_drawer: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedDateKey]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiURL}/pos/transactions/daybook`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDateKey },
      });
      if (response.data.success) {
        setStats({
          upi_collection: safeNumber(response.data.stats?.upi_collection),
          bank_transfer: safeNumber(response.data.stats?.bank_transfer),
          cash_collection: safeNumber(response.data.stats?.cash_collection),
          company_billing: safeNumber(response.data.stats?.company_billing),
          expenses: safeNumber(response.data.stats?.expenses),
          cash_on_drawer: safeNumber(response.data.stats?.cash_on_drawer),
        });
      }
    } catch (error) {
      message.error("Error fetching stats.");
    }
  }, [selectedDateKey]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  useEffect(() => {
    const handleStorageChange = () => {
      const newBusinessDate = localStorage.getItem("posbusiness_date");
      if (newBusinessDate) {
        const newDate = moment(newBusinessDate);
        if (newDate.isValid() && !newDate.isSame(selectedDate, "day")) setSelectedDate(newDate);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("posbusinessdate:changed", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("posbusinessdate:changed", handleStorageChange);
    };
  }, [selectedDate]);

  const debouncedSearch = useMemo(() => debounce((value) => setSearchText(value), 250), []);
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleDateChange = (dateOrDirection) => {
    let nextDate;
    if (dateOrDirection === "prev") nextDate = selectedDate.clone().subtract(1, "days");
    else if (dateOrDirection === "next") nextDate = selectedDate.clone().add(1, "days");
    else if (dateOrDirection === "today") nextDate = moment();
    else nextDate = moment(dateOrDirection);
    if (!nextDate.isValid()) return;
    const formatted = savePosBusinessDate(nextDate);
    setSelectedDate(moment(formatted));
    window.dispatchEvent(new Event("posbusinessdate:changed"));
  };

  const filteredTransactions = useMemo(() => {
    let filtered = [...allTransactions];
    if (searchText) {
      filtered = filtered.filter((txn) => `${txn.fullname || ""} ${txn.category || ""} ${txn.payment_reference || ""} ${txn.payment_mode || ""} ${txn.source || ""}`.toLowerCase().includes(searchText.toLowerCase()));
    }
    if (paymentModeFilter) filtered = filtered.filter((txn) => txn.payment_mode === paymentModeFilter);
    return filtered;
  }, [allTransactions, searchText, paymentModeFilter]);

  const cashInTransactions = useMemo(() => filteredTransactions.filter((txn) => txn.transaction_type === "CASH_IN"), [filteredTransactions]);
  const cashOutTransactions = useMemo(() => filteredTransactions.filter((txn) => txn.transaction_type === "CASH_OUT"), [filteredTransactions]);
  const cashInTotal = useMemo(() => cashInTransactions.reduce((sum, txn) => sum + safeNumber(txn.amount), 0), [cashInTransactions]);
  const cashOutTotal = useMemo(() => cashOutTransactions.reduce((sum, txn) => sum + safeNumber(txn.amount), 0), [cashOutTransactions]);
  const netMovement = cashInTotal - cashOutTotal;

  const footerData = [
    { key: "UPI Collection", value: formatMoney(stats.upi_collection, currency) },
    { key: "Bank Transfer", value: formatMoney(stats.bank_transfer, currency) },
    { key: "Cash Collection", value: formatMoney(stats.cash_collection, currency) },
    { key: "Company Credit", value: formatMoney(stats.company_billing, currency) },
    { key: "Expenses", value: formatMoney(stats.expenses, currency) },
    { key: "Cash on Drawer", value: formatMoney(stats.cash_on_drawer, currency) },
    { key: "Total Cash In", value: formatMoney(cashInTotal, currency) },
    { key: "Total Cash Out", value: formatMoney(cashOutTotal, currency) },
  ];

  const exportRows = [...cashInTransactions, ...cashOutTransactions];
  const handleExportXLS = () => exportService.exportToXLS(exportRows, headers, `day_book_${selectedDate.format("YYYYMMDD")}.xlsx`, "Day Book", footerData);
  const handleExportCSV = () => exportService.exportToCSV(exportRows, headers, `day_book_${selectedDate.format("YYYYMMDD")}.csv`, footerData);
  const handleExportPDF = () => setPreviewVisible(true);
  const handlePrint = () => setPreviewVisible(true);

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={handleExportXLS}>Download XLS</Menu.Item>
      <Menu.Item key="csv" onClick={handleExportCSV}>Download CSV</Menu.Item>
      <Menu.Item key="pdf" onClick={handleExportPDF}>Download PDF</Menu.Item>
      <Menu.Item key="print" onClick={handlePrint}>Print Preview</Menu.Item>
    </Menu>
  );

  const MIN_ROWS = 10;
  const maxRows = Math.max(Math.max(cashInTransactions.length, cashOutTransactions.length), MIN_ROWS);
  const balancedCashInTransactions = [...cashInTransactions];
  const balancedCashOutTransactions = [...cashOutTransactions];
  for (let i = cashInTransactions.length; i < maxRows; i++) balancedCashInTransactions.push({ key: `empty-in-${i}`, category: "", payment_mode: "", amount: "", payment_reference: "", isEmptyRow: true });
  for (let i = cashOutTransactions.length; i < maxRows; i++) balancedCashOutTransactions.push({ key: `empty-out-${i}`, category: "", payment_mode: "", amount: "", payment_reference: "", isEmptyRow: true });

  const handleAddOrEditExpense = (data) => {
    const { expense, isEdit } = data;
    const token = localStorage.getItem("token");
    const newOrUpdatedExpense = {
      ...expense,
      amount: safeNumber(expense.amount),
      transaction_id: expense.transaction_id || Date.now(),
      transaction_type: expense.transaction_type || "CASH_OUT",
      source: expense.source || "EXPENSE",
    };
    if (!isEdit) {
      setAllTransactions([newOrUpdatedExpense, ...allTransactions]);
      setTransactions([newOrUpdatedExpense, ...transactions]);
    } else {
      const updatedTransactions = allTransactions.map((txn) => txn.transaction_id === newOrUpdatedExpense.transaction_id ? newOrUpdatedExpense : txn);
      setAllTransactions(updatedTransactions);
      setTransactions(updatedTransactions);
    }
    setIsExpensePopupVisible(false);
    setSelectedExpense(null);
    axios.post(isEdit ? `${apiURL}/transactions/edit` : `${apiURL}/pos/transactions/add-expenses`, newOrUpdatedExpense, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => { message.success(`Expense ${isEdit ? "updated" : "added"} successfully!`); fetchStats(); })
      .catch((error) => { message.error(`Failed to ${isEdit ? "update" : "add"} expense. Reverting changes...`); console.error(`Error ${isEdit ? "updating" : "adding"} expense:`, error); fetchTransactions(); });
  };

  const commonColumns = [
    { title: "Particulars", dataIndex: "category", key: "particulars", ellipsis: true, render: (text, record) => record.isEmptyRow ? "" : <span className="ns-daybook-particulars">{record.payment_reference ? `${record.payment_reference} · ` : ""}{text || record.source || ""}</span> },
    { title: "Mode", dataIndex: "payment_mode", key: "payment_mode", width: 130, render: (text, record) => record.isEmptyRow ? "" : <span className="ns-daybook-mode">{text || "—"}</span> },
    { title: "Amount", dataIndex: "amount", key: "amount", width: 140, align: "right", render: (text, record) => record.isEmptyRow ? "" : <strong className="ns-daybook-money">{formatMoney(text, currency)}</strong> },
    { title: "Reference", dataIndex: "payment_reference", key: "payment_reference", width: 150, ellipsis: true, render: (text, record) => record.isEmptyRow ? "" : text || "—" },
  ];

  const cashOutColumns = [
    ...commonColumns,
    { title: "Actions", key: "actions", width: 92, align: "center", render: (_, record) => !record.isEmptyRow && record.source === "EXPENSE" ? <Button type="link" className="ns-daybook-edit-link" onClick={() => { setSelectedExpense(record); setIsExpensePopupVisible(true); }}>Edit</Button> : null },
  ];

  const dayBookStats = [
    { label: "UPI Collection", value: stats.upi_collection, icon: <CreditCardOutlined />, tone: "#2563eb" },
    { label: "Bank Transfer", value: stats.bank_transfer, icon: <BankOutlined />, tone: "#06b6d4" },
    { label: "Cash Collection", value: stats.cash_collection, icon: <WalletOutlined />, tone: "#16a34a" },
    { label: "Company Credit", value: stats.company_billing, icon: <DollarCircleOutlined />, tone: "#7c3aed" },
    { label: "Expenses", value: stats.expenses, icon: <ReceiptOutlined />, tone: "#dc2626" },
    { label: "Cash on Drawer", value: stats.cash_on_drawer, icon: <WalletOutlined />, tone: "#f59e0b" },
  ];

  const dayBookStyles = `
    .ns-daybook-page-wrapper { background: radial-gradient(circle at 8% 0%, rgba(37,99,235,.07), transparent 30%), radial-gradient(circle at 92% 2%, rgba(34,197,94,.08), transparent 28%), #f8fafc; min-height: calc(100vh - 60px); }
    .ns-daybook-page { padding-top: 12px !important; padding-bottom: 24px; color: #0f172a; }
    .ns-daybook-hero { background: radial-gradient(circle at top left, rgba(37,99,235,.14), transparent 34%), radial-gradient(circle at top right, rgba(34,197,94,.10), transparent 32%), linear-gradient(135deg,#fff 0%,#f8fbff 100%); border:1px solid rgba(15,23,42,.08); border-radius:24px; box-shadow:0 16px 42px rgba(15,23,42,.075); padding:17px 18px; margin-bottom:12px; display:flex; align-items:flex-start; justify-content:space-between; gap:16px; flex-wrap:wrap; }
    .ns-daybook-hero-left { display:flex; align-items:flex-start; gap:13px; min-width:0; }
    .ns-daybook-hero-icon { width:52px; height:52px; border-radius:19px; background:linear-gradient(135deg,#1d4ed8,#2563eb); color:#fff; display:inline-flex; align-items:center; justify-content:center; box-shadow:0 16px 34px rgba(37,99,235,.22); flex:0 0 auto; font-size:1.32rem; }
    .ns-daybook-eyebrow { color:#2563eb; font-size:.72rem; letter-spacing:.14em; text-transform:uppercase; font-weight:950; margin-bottom:5px; }
    .ns-daybook-hero h2 { color:#001f5b; font-size:1.42rem; line-height:1.16; font-weight:950; margin:0; }
    .ns-daybook-hero p { color:#64748b; font-size:.82rem; line-height:1.5; font-weight:760; margin:7px 0 0; max-width:820px; }
    .ns-daybook-actions, .ns-daybook-toolbar-right { display:flex; align-items:center; justify-content:flex-end; gap:8px; flex-wrap:wrap; }
    .ns-daybook-date-pill { min-height:42px; border-radius:16px; background:#fff; border:1px solid rgba(15,23,42,.10); box-shadow:0 10px 22px rgba(15,23,42,.06); padding:4px 7px; display:inline-flex; align-items:center; gap:7px; }
    .ns-daybook-date-copy { min-width:134px; text-align:center; display:grid; gap:1px; }
    .ns-daybook-date-copy span { color:#94a3b8; font-size:.62rem; text-transform:uppercase; letter-spacing:.07em; font-weight:950; line-height:1; }
    .ns-daybook-date-copy strong { color:#0f172a; font-size:.86rem; line-height:1.1; font-weight:950; }
    .ns-daybook-btn { min-height:42px !important; border-radius:14px !important; font-weight:950 !important; display:inline-flex !important; align-items:center !important; justify-content:center !important; gap:8px !important; padding:0 16px !important; }
    .ns-daybook-primary { background:linear-gradient(135deg,#1d4ed8,#2563eb) !important; border-color:#1d4ed8 !important; color:#fff !important; box-shadow:0 14px 30px rgba(37,99,235,.18) !important; }
    .ns-daybook-soft { background:#fff !important; color:#334155 !important; border:1px solid rgba(15,23,42,.10) !important; }
    .ns-daybook-date-picker .ant-picker { min-height:42px; border-radius:14px; border-color:rgba(15,23,42,.10); font-weight:900; width:155px; }
    .ns-daybook-toolbar { background:#fff; border:1px solid rgba(15,23,42,.08); border-radius:22px; box-shadow:0 12px 30px rgba(15,23,42,.06); padding:12px; margin-bottom:12px; display:grid; grid-template-columns:minmax(260px,1fr) auto; gap:10px; align-items:center; }
    .ns-daybook-search .ant-input { min-height:42px; border-radius:15px 0 0 15px !important; font-weight:760; }
    .ns-daybook-search .ant-btn { min-height:42px; border-radius:0 15px 15px 0 !important; font-weight:950; display:inline-flex; align-items:center; justify-content:center; }
    .ns-daybook-stats-grid { display:grid; grid-template-columns:repeat(6,minmax(0,1fr)); gap:10px; margin-bottom:12px; }
    .ns-daybook-stat { background:#fff; border:1px solid rgba(15,23,42,.08); border-top:4px solid var(--tone,#2563eb); border-radius:20px; box-shadow:0 12px 30px rgba(15,23,42,.06); padding:13px 14px; min-width:0; }
    .ns-daybook-stat-top { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:10px; }
    .ns-daybook-stat-label { color:#64748b; display:block; font-size:.66rem; font-weight:950; letter-spacing:.06em; text-transform:uppercase; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .ns-daybook-stat-icon { width:34px; height:34px; border-radius:13px; color:var(--tone,#2563eb); background:#f8fafc; display:inline-flex; align-items:center; justify-content:center; flex:0 0 auto; }
    .ns-daybook-stat strong { color:#0f172a; display:block; font-size:1.08rem; line-height:1.12; font-weight:950; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .ns-daybook-ledger-shell, .ns-daybook-loader { background:#fff; border:1px solid rgba(15,23,42,.08); border-radius:24px; box-shadow:0 16px 42px rgba(15,23,42,.075); overflow:hidden; }
    .ns-daybook-ledger-head { padding:15px 17px; border-bottom:1px solid rgba(15,23,42,.08); background:radial-gradient(circle at top right,rgba(37,99,235,.08),transparent 28%),#fff; display:flex; justify-content:space-between; align-items:flex-start; gap:14px; flex-wrap:wrap; }
    .ns-daybook-ledger-head h3 { color:#001f5b; font-size:1.05rem; line-height:1.18; font-weight:950; margin:0; }
    .ns-daybook-ledger-head p { color:#64748b; font-size:.76rem; line-height:1.42; font-weight:760; margin:5px 0 0; }
    .ns-daybook-flow-summary { display:flex; gap:8px; flex-wrap:wrap; align-items:center; justify-content:flex-end; }
    .ns-daybook-flow-chip { min-height:32px; border-radius:999px; padding:6px 10px; background:#f8fafc; color:#334155; border:1px solid rgba(15,23,42,.08); font-size:.72rem; font-weight:950; display:inline-flex; align-items:center; gap:6px; }
    .ns-daybook-flow-chip.in { background:#ecfdf5; color:#047857; border-color:#bbf7d0; }
    .ns-daybook-flow-chip.out { background:#fff1f2; color:#be123c; border-color:#fecdd3; }
    .ns-daybook-ledger-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); background:#f8fafc; gap:1px; padding:1px; }
    .ns-daybook-flow-card { background:#fff; min-width:0; overflow:hidden; }
    .ns-daybook-flow-head { padding:13px 15px; border-bottom:1px solid rgba(15,23,42,.08); display:flex; justify-content:space-between; gap:10px; align-items:center; position:relative; }
    .ns-daybook-flow-head::after { content:""; position:absolute; left:0; right:0; bottom:0; height:4px; background:var(--tone,#16a34a); }
    .ns-daybook-flow-title { display:flex; align-items:center; gap:9px; min-width:0; }
    .ns-daybook-flow-title strong { color:#0f172a; font-size:.86rem; line-height:1.2; font-weight:950; text-transform:uppercase; letter-spacing:.04em; }
    .ns-daybook-flow-title span { color:#64748b; display:block; font-size:.72rem; font-weight:760; margin-top:2px; }
    .ns-daybook-flow-total { color:var(--tone,#16a34a); font-size:.98rem; font-weight:950; white-space:nowrap; }
    .ns-daybook-table .ant-table-thead > tr > th { background:#f8fafc !important; color:#0f172a !important; font-size:.7rem; text-transform:uppercase; letter-spacing:.06em; font-weight:950 !important; padding:10px 12px !important; border-bottom:1px solid rgba(15,23,42,.08) !important; }
    .ns-daybook-table .ant-table-tbody > tr > td { padding:11px 12px !important; font-size:.82rem; min-height:52px; height:52px; color:#334155; border-bottom:1px solid rgba(15,23,42,.055) !important; font-weight:760; }
    .ns-daybook-table .ant-table-tbody > tr:hover > td { background:#eff6ff !important; }
    .ns-daybook-table .ant-table-summary > tr > td { background:#f8fafc !important; color:#0f172a; font-weight:950; border-top:1px solid rgba(15,23,42,.08); }
    .ns-daybook-mode { min-height:24px; border-radius:999px; padding:4px 8px; background:#f1f5f9; color:#475569; display:inline-flex; align-items:center; justify-content:center; font-size:.68rem; font-weight:950; }
    .ns-daybook-money, .ns-daybook-particulars { color:#0f172a; font-weight:950; }
    .ns-daybook-edit-link { padding:0 !important; height:auto !important; font-weight:950 !important; }
    .ns-daybook-loader { min-height:360px; color:#64748b; display:flex; flex-direction:column; align-items:center; justify-content:center; margin-top:12px; }
    .ns-daybook-dots { display:flex; gap:8px; justify-content:center; margin-bottom:14px; }
    .ns-daybook-dot { width:10px; height:10px; background:#2563eb; border-radius:50%; animation:ns-daybook-bounce .58s infinite alternate; }
    .ns-daybook-dot:nth-child(2){animation-delay:.1s}.ns-daybook-dot:nth-child(3){animation-delay:.2s}.ns-daybook-dot:nth-child(4){animation-delay:.3s}.ns-daybook-dot:nth-child(5){animation-delay:.4s}
    @keyframes ns-daybook-bounce { to { transform: translateY(-10px); } }
    .ns-daybook-swal { border-radius:22px !important; }
    @media (max-width:1320px){ .ns-daybook-stats-grid{grid-template-columns:repeat(3,minmax(0,1fr));}.ns-daybook-toolbar{grid-template-columns:1fr}.ns-daybook-toolbar-right{justify-content:flex-start} }
    @media (max-width:1080px){ .ns-daybook-ledger-grid{grid-template-columns:1fr} }
    @media (max-width:720px){ .ns-daybook-page{padding-left:8px;padding-right:8px}.ns-daybook-actions,.ns-daybook-toolbar-right{width:100%;justify-content:flex-start}.ns-daybook-date-pill,.ns-daybook-date-picker,.ns-daybook-btn,.ns-daybook-toolbar-right>div,.ns-daybook-toolbar-right .ant-btn{width:100% !important}.ns-daybook-stats-grid{grid-template-columns:repeat(2,minmax(0,1fr))} }
    @media (max-width:520px){ .ns-daybook-stats-grid{grid-template-columns:1fr}.ns-daybook-date-pill{flex-wrap:wrap}.ns-daybook-date-copy{flex:1 1 120px} }
  `;

  return (
    <div className="page-wrapper ns-daybook-page-wrapper">
      <style>{dayBookStyles}</style>
      <div className="content container-fluid ns-daybook-page">
        <section className="ns-daybook-hero">
          <div className="ns-daybook-hero-left">
            <span className="ns-daybook-hero-icon"><i className="fas fa-book" /></span>
            <div><div className="ns-daybook-eyebrow">POS Cash Movement</div><h2>Day Book</h2><p>Track collections, expenses, cash movement, and payment modes for the active POS business date.</p></div>
          </div>
          <div className="ns-daybook-actions">
            <div className="ns-daybook-date-pill">
              <Tooltip title="Previous day"><Button shape="circle" className="ns-daybook-soft" onClick={() => handleDateChange("prev")} icon={<i className="fas fa-chevron-left" />} /></Tooltip>
              <div className="ns-daybook-date-copy"><span>Business Date</span><strong>{selectedDate.format("DD MMM YYYY")}</strong></div>
              <Tooltip title="Next day"><Button shape="circle" className="ns-daybook-soft" onClick={() => handleDateChange("next")} icon={<i className="fas fa-chevron-right" />} /></Tooltip>
            </div>
            <Button className="ns-daybook-btn ns-daybook-soft" icon={<ReloadOutlined />} onClick={fetchTransactions}>Refresh</Button>
            <Button className="ns-daybook-btn ns-daybook-primary" icon={<PlusOutlined />} onClick={() => { setSelectedExpense(null); setIsExpensePopupVisible(true); }}>Add Expense</Button>
          </div>
        </section>

        <DayBookPreview visible={previewVisible} onClose={() => setPreviewVisible(false)} reportTitle="Day Book" propertyName="Your Property Name" cashInData={cashInTransactions} cashOutData={cashOutTransactions} headers={headers} footerData={footerData} totalColumns={["amount"]} onDownloadPDF={handleExportPDF} apiEndpoint="pos/transactions/generate-pdf" apiUrl={apiURL} />

        <section className="ns-daybook-toolbar">
          <div className="ns-daybook-search"><Search placeholder="Search particulars, reference, source, or payment mode..." allowClear enterButton={<SearchOutlined />} size="large" value={searchInput} onChange={(event) => handleSearchChange(event.target.value)} /></div>
          <div className="ns-daybook-toolbar-right">
            <Select options={paymentModes} placeholder="Payment Mode" isClearable value={paymentModes.find((mode) => mode.value === paymentModeFilter) || null} styles={selectStyles} menuPortalTarget={typeof document !== "undefined" ? document.body : null} menuPosition="fixed" onChange={(option) => setPaymentModeFilter(option ? option.value : null)} />
            <Button className="ns-daybook-btn ns-daybook-soft" onClick={() => handleDateChange("today")}>Today</Button>
            <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight"><Button className="ns-daybook-btn ns-daybook-soft" icon={<DownloadOutlined />}>Download <DownOutlined /></Button></Dropdown>
          </div>
        </section>

        <section className="ns-daybook-stats-grid">{dayBookStats.map((stat) => <article className="ns-daybook-stat" style={{ "--tone": stat.tone }} key={stat.label}><div className="ns-daybook-stat-top"><span className="ns-daybook-stat-label">{stat.label}</span><span className="ns-daybook-stat-icon">{stat.icon}</span></div><strong>{formatMoney(stat.value, currency)}</strong></article>)}</section>

        {loading ? (
          <div className="ns-daybook-loader"><div className="ns-daybook-dots"><div className="ns-daybook-dot" /><div className="ns-daybook-dot" /><div className="ns-daybook-dot" /><div className="ns-daybook-dot" /><div className="ns-daybook-dot" /></div><strong>Loading day book...</strong><span>Preparing transactions and cash movement for {selectedDate.format("DD MMM YYYY")}.</span></div>
        ) : (
          <section className="ns-daybook-ledger-shell">
            <div className="ns-daybook-ledger-head"><div><div className="ns-daybook-eyebrow">Ledger View</div><h3>Cash In / Cash Out Flow</h3><p>Balanced ledger columns help the cashier quickly compare inflow, outflow, and drawer movement.</p></div><div className="ns-daybook-flow-summary"><span className="ns-daybook-flow-chip in">In: {formatMoney(cashInTotal, currency)}</span><span className="ns-daybook-flow-chip out">Out: {formatMoney(cashOutTotal, currency)}</span><span className="ns-daybook-flow-chip">Net: {formatMoney(netMovement, currency)}</span></div></div>
            <div className="ns-daybook-ledger-grid">
              <div className="ns-daybook-flow-card"><div className="ns-daybook-flow-head" style={{ "--tone": "#16a34a" }}><div className="ns-daybook-flow-title"><ArrowDownCircle size={19} color="#16a34a" /><div><strong>Transaction In Flow</strong><span>{cashInTransactions.length} transaction{cashInTransactions.length === 1 ? "" : "s"}</span></div></div><span className="ns-daybook-flow-total">{formatMoney(cashInTotal, currency)}</span></div><Table dataSource={balancedCashInTransactions} columns={commonColumns} rowKey={(record) => record.transaction_id || record.key} pagination={false} className="ns-daybook-table" locale={{ emptyText: <Empty description="No cash-in transactions" /> }} scroll={{ x: 620 }} summary={() => <Table.Summary.Row><Table.Summary.Cell index={0} colSpan={2}>Total In Flow</Table.Summary.Cell><Table.Summary.Cell index={2} align="right">{formatMoney(cashInTotal, currency)}</Table.Summary.Cell><Table.Summary.Cell index={3} /></Table.Summary.Row>} /></div>
              <div className="ns-daybook-flow-card"><div className="ns-daybook-flow-head" style={{ "--tone": "#dc2626" }}><div className="ns-daybook-flow-title"><ArrowUpCircle size={19} color="#dc3545" /><div><strong>Transaction Out Flow</strong><span>{cashOutTransactions.length} transaction{cashOutTransactions.length === 1 ? "" : "s"}</span></div></div><span className="ns-daybook-flow-total" style={{ color: "#dc2626" }}>{formatMoney(cashOutTotal, currency)}</span></div><Table dataSource={balancedCashOutTransactions} columns={cashOutColumns} rowKey={(record) => record.transaction_id || record.key} pagination={false} className="ns-daybook-table" locale={{ emptyText: <Empty description="No cash-out transactions" /> }} scroll={{ x: 720 }} summary={() => <Table.Summary.Row><Table.Summary.Cell index={0} colSpan={2}>Total Out Flow</Table.Summary.Cell><Table.Summary.Cell index={2} align="right">{formatMoney(cashOutTotal, currency)}</Table.Summary.Cell><Table.Summary.Cell index={3} /><Table.Summary.Cell index={4} /></Table.Summary.Row>} /></div>
            </div>
          </section>
        )}

        {isExpensePopupVisible && <AddEditExpensePopup onSubmit={handleAddOrEditExpense} expenseData={selectedExpense} onCancel={() => { setIsExpensePopupVisible(false); setSelectedExpense(null); }} />}
      </div>
    </div>
  );
};

export default DayBook;
